// Batch placement of existing dishes on a menu section ("Plat existant",
// AddExistingDishModal). Each dish is a separate POST /api/menu_items; one
// failure must not stop the others, and the user is told what happened to
// each dish (SFRESTO-150).

import { getApiErrorMessage } from "./apiErrors";

interface DishLike {
  id: string;
  title: string;
}

export interface FailedDishPlacement<T extends DishLike> {
  dish: T;
  /** French reason, safe to show (never a raw translation key). */
  reason: string;
}

export interface DishPlacementResult<T extends DishLike> {
  placed: T[];
  failed: FailedDishPlacement<T>[];
}

const PLACEMENT_ERROR_MESSAGES: Record<string, string> = {
  "validation.menu_item.error.title.exists":
    "ce plat existe déjà dans cette section",
  "validation.menu_item.error.title.not_blank":
    "le nom du plat est obligatoire",
  "validation.menu_item.error.base_price.not_blank":
    "le prix du plat est invalide",
  "validation.menu_item.error.base_price.invalid":
    "le prix du plat est invalide",
  "validation.menu_item.error.menu_section.not_blank": "section introuvable",
};

const FALLBACK_REASON = "une erreur est survenue";

/**
 * French reason for a failed POST /api/menu_items. The lower-case reasons
 * above fit the summary sentence; other known keys use the shared map
 * (`getApiErrorMessage`), anything else the generic reason.
 */
export function translateDishPlacementError(error: unknown): string {
  return getApiErrorMessage(error, FALLBACK_REASON, {
    messages: PLACEMENT_ERROR_MESSAGES,
    forbidden: "vous n'avez pas l'autorisation de placer ce plat",
  });
}

/**
 * Places dishes one after the other and keeps going after a failure.
 * `position` is the number of dishes placed so far, so the caller can give
 * each new placement the next sortOrder without gaps.
 */
export async function placeDishesSequentially<T extends DishLike>(
  dishes: T[],
  placeOne: (dish: T, position: number) => Promise<unknown>
): Promise<DishPlacementResult<T>> {
  const placed: T[] = [];
  const failed: FailedDishPlacement<T>[] = [];
  for (const dish of dishes) {
    try {
      await placeOne(dish, placed.length);
      placed.push(dish);
    } catch (error) {
      failed.push({ dish, reason: translateDishPlacementError(error) });
    }
  }
  return { placed, failed };
}

export interface DishPlacementSummary {
  kind: "success" | "partial" | "error";
  message: string;
}

/** One French toast message describing the outcome for every dish. */
export function summarizeDishPlacement<T extends DishLike>({
  placed,
  failed,
}: DishPlacementResult<T>): DishPlacementSummary {
  const failures = failed.map(
    ({ dish, reason }) => `« ${dish.title} » : ${reason}`
  );

  if (failed.length === 0) {
    return {
      kind: "success",
      message:
        placed.length > 1
          ? `${placed.length} plats ajoutés à cette section`
          : `« ${placed[0]?.title ?? ""} » ajouté à cette section`,
    };
  }

  if (placed.length === 0) {
    return {
      kind: "error",
      message:
        failed.length === 1
          ? `Impossible d'ajouter ${failures[0]}`
          : ["Aucun plat ajouté", ...failures].join(" · "),
    };
  }

  const added =
    placed.length > 1
      ? `${placed.length} plats ajoutés`
      : `« ${placed[0].title} » ajouté`;
  return { kind: "partial", message: [added, ...failures].join(" · ") };
}
